import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Card, Container, Row, Col, Button, ListGroup, Spinner, Badge,
  Form, InputGroup, Table, Pagination
} from 'react-bootstrap';
import { getStoreDetails, getStoreInventory } from '../services/priceService';
import { useHomeLocation } from '../contexts/HomeLocationContext';
import ReportButton from './ReportButton';

const PAGE_SIZE = 50;
const PROMO_PAGE_SIZE = 30;

function PriceCell({ price, promotionPrice, isConditional }) {
  if (!isConditional && promotionPrice && promotionPrice < price) {
    return (
      <span>
        <span style={{ textDecoration: 'line-through', color: 'var(--apple-text-3)', marginRight: 6, fontSize: '0.85em' }}>
          ₪{price.toFixed(2)}
        </span>
        <span style={{ color: 'var(--apple-accent)', fontWeight: 600 }}>₪{promotionPrice.toFixed(2)}</span>
      </span>
    );
  }
  return <span>₪{price.toFixed(2)}</span>;
}

function PromoBadge({ item }) {
  if (!item.promotionPrice) return null;
  const isConditional = item.promotionType === 'conditional_discount';
  const pct = item.promotionSavingsPercentage;
  if (isConditional) {
    const basket = item.promotionMinBasket ? `עם קנייה מ-₪${item.promotionMinBasket}` : 'בתנאי';
    return (
      <Badge
        bg="light"
        text="secondary"
        className="promo-badge"
        style={{ verticalAlign: 'middle', marginLeft: 4, border: '1px solid #ccc' }}
        title={item.promotionDescription || ''}
      >
        ₪{parseFloat(item.promotionPrice).toFixed(2)} {basket}
      </Badge>
    );
  }
  return (
    <Badge bg="danger" className="promo-badge" style={{ verticalAlign: 'middle', marginLeft: 4 }}>
      {pct ? `-${Math.round(pct)}%` : 'מבצע'}
    </Badge>
  );
}

function Paginator({ page, total, pageSize, onChange }) {
  const totalPages = Math.ceil(total / pageSize);
  if (totalPages <= 1) return null;

  const items = [];
  for (let i = 1; i <= totalPages; i++) {
    const near = Math.abs(i - page) <= 2 || i === 1 || i === totalPages;
    if (!near) {
      if (items[items.length - 1]?.key !== `ell-${i}`) {
        items.push(<Pagination.Ellipsis key={`ell-${i}`} disabled />);
      }
      continue;
    }
    items.push(
      <Pagination.Item key={i} active={i === page} onClick={() => onChange(i)}>
        {i}
      </Pagination.Item>
    );
  }

  return (
    <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
      <small className="text-muted">
        {((page - 1) * pageSize) + 1}–{Math.min(page * pageSize, total)} מתוך {total.toLocaleString()} פריטים
      </small>
      <Pagination size="sm" className="mb-0">
        <Pagination.Prev disabled={page === 1} onClick={() => onChange(page - 1)} />
        {items}
        <Pagination.Next disabled={page === totalPages} onClick={() => onChange(page + 1)} />
      </Pagination>
    </div>
  );
}

function ProductsTab({ storeId, chain, navigate }) {
  const [data, setData] = useState({ total: 0, items: [] });
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  const fetchData = useCallback(async (currentPage, currentSearch) => {
    if (!chain) return;
    setLoading(true);
    const result = await getStoreInventory(storeId, chain, {
      page: currentPage,
      pageSize: PAGE_SIZE,
      search: currentSearch,
    });
    setData(result);
    setLoading(false);
  }, [storeId, chain]);

  useEffect(() => {
    fetchData(page, search);
  }, [fetchData, page, search]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchInput(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch(val);
      setPage(1);
    }, 350);
  };

  const handleClear = () => {
    clearTimeout(debounceRef.current);
    setSearchInput('');
    setSearch('');
    setPage(1);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    clearTimeout(debounceRef.current);
    setSearch(searchInput);
    setPage(1);
  };

  if (!chain) {
    return <div className="text-center text-muted py-4">מידע רשת לא זמין עבור חנות זו</div>;
  }

  return (
    <div>
      <Form onSubmit={handleSubmit} className="mb-3">
        <InputGroup>
          <Form.Control
            placeholder="חפש לפי שם מוצר או ברקוד..."
            value={searchInput}
            onChange={handleSearchChange}
            dir="rtl"
          />
          {searchInput && (
            <Button variant="outline-secondary" onClick={handleClear} type="button">✕</Button>
          )}
          <Button variant="outline-primary" type="submit">חיפוש</Button>
        </InputGroup>
      </Form>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" size="sm" className="me-2" />
          <span>טוען מוצרים...</span>
        </div>
      ) : data.items.length === 0 ? (
        <div className="text-center text-muted py-4">
          {search ? `לא נמצאו מוצרים עבור "${search}"` : 'לא נמצאו מוצרים עבור חנות זו'}
        </div>
      ) : (
        <>
          <Table hover responsive size="sm" className="mb-0">
            <thead className="table-light">
              <tr>
                <th style={{ minWidth: 220 }}>שם מוצר</th>
                <th>מחיר</th>
                <th>מחיר ליחידה</th>
                <th>ברקוד</th>
                <th>יצרן</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map(item => (
                <tr
                  key={item.itemCode}
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/?tab=search&q=${item.itemCode}`)}
                >
                  <td dir="rtl">
                    {item.itemName}
                    <PromoBadge item={item} />
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <PriceCell price={item.price} promotionPrice={item.promotionPrice} isConditional={item.promotionType === 'conditional_discount'} />
                    <ReportButton itemCode={item.itemCode} storeId={storeId} chain={chain} price={item.promotionPrice && item.promotionPrice < item.price ? item.promotionPrice : item.price} itemName={item.itemName} />
                  </td>
                  <td style={{ whiteSpace: 'nowrap', color: '#666', fontSize: '0.85em' }}>
                    {item.unitPrice ? `₪${item.unitPrice.toFixed(2)}/${item.unitMeasure || '?'}` : '—'}
                  </td>
                  <td style={{ fontFamily: 'monospace', fontSize: '0.85em', color: '#888' }}>
                    {item.itemCode}
                  </td>
                  <td style={{ fontSize: '0.85em', color: '#666' }} dir="rtl">
                    {item.manufacturer || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <Paginator page={page} total={data.total} pageSize={PAGE_SIZE} onChange={setPage} />
        </>
      )}
    </div>
  );
}

function PromotionsTab({ storeId, chain, navigate }) {
  const [data, setData] = useState({ total: 0, items: [] });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('savings_pct');
  const debounceRef = useRef(null);

  const fetchData = useCallback(async (currentPage, currentSearch, currentSort) => {
    if (!chain) return;
    setLoading(true);
    const result = await getStoreInventory(storeId, chain, {
      page: currentPage,
      pageSize: PROMO_PAGE_SIZE,
      promotionsOnly: true,
      search: currentSearch,
      sortBy: currentSort,
    });
    setData(result);
    setLoading(false);
  }, [storeId, chain]);

  useEffect(() => {
    fetchData(page, search, sortBy);
  }, [fetchData, page, search, sortBy]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchInput(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch(val);
      setPage(1);
    }, 350);
  };

  const handleClear = () => {
    clearTimeout(debounceRef.current);
    setSearchInput('');
    setSearch('');
    setPage(1);
  };

  const handleSortChange = (e) => {
    setSortBy(e.target.value);
    setPage(1);
  };

  if (!chain) {
    return <div className="text-center text-muted py-4">מידע רשת לא זמין עבור חנות זו</div>;
  }

  return (
    <div>
      <div className="d-flex gap-2 mb-3 flex-wrap">
        <InputGroup style={{ maxWidth: 320 }}>
          <Form.Control
            placeholder="חפש לפי שם מוצר או ברקוד..."
            value={searchInput}
            onChange={handleSearchChange}
            dir="rtl"
          />
          {searchInput && (
            <Button variant="outline-secondary" onClick={handleClear} type="button">✕</Button>
          )}
        </InputGroup>
        <Form.Select style={{ maxWidth: 220 }} value={sortBy} onChange={handleSortChange}>
          <option value="savings_pct">מיון: % חיסכון (גבוה ראשון)</option>
          <option value="savings_amt">מיון: סכום חיסכון (גבוה ראשון)</option>
          <option value="price_asc">מיון: מחיר (נמוך ראשון)</option>
          <option value="price_desc">מיון: מחיר (גבוה ראשון)</option>
          <option value="name">מיון: שם (א-ת)</option>
        </Form.Select>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" size="sm" className="me-2" />
          <span>טוען מבצעים...</span>
        </div>
      ) : data.items.length === 0 ? (
        <div className="text-center text-muted py-5">
          <div style={{ fontSize: '2.5rem', marginBottom: 8 }}>🏷️</div>
          <p>{search ? `לא נמצאו מבצעים עבור "${search}"` : 'לא נמצאו מבצעים פעילים בחנות זו'}</p>
        </div>
      ) : null}

      {!loading && data.items.length > 0 && (
        <>
          <Row xs={1} md={2} lg={3} className="g-3 mb-3">
            {data.items.map(item => (
              <Col key={item.itemCode}>
                <Card
                  className="h-100 store-card"
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/?tab=search&q=${item.itemCode}`)}
                >
                  <Card.Body className="p-3">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <Badge bg="danger" className="promo-badge">
                        {item.promotionSavingsPercentage
                          ? `חסכון ${Math.round(item.promotionSavingsPercentage)}%`
                          : 'מבצע'}
                      </Badge>
                      {item.promotionMinQty > 1 && (
                        <Badge bg="warning">
                          ×{item.promotionMinQty}
                        </Badge>
                      )}
                    </div>
                    <div dir="rtl" style={{ fontWeight: 500, marginBottom: 4, lineHeight: 1.3, color: 'var(--apple-text)' }}>
                      {item.itemName}
                    </div>
                    {item.manufacturer && (
                      <div dir="rtl" style={{ fontSize: '0.8em', color: 'var(--apple-text-2)', marginBottom: 6 }}>
                        {item.manufacturer}
                      </div>
                    )}
                    <div className="d-flex align-items-baseline gap-2 flex-wrap mb-1">
                      <span style={{ textDecoration: 'line-through', color: 'var(--apple-text-3)', fontSize: '0.9em' }}>
                        ₪{item.price.toFixed(2)}
                      </span>
                      <span style={{ color: 'var(--apple-accent)', fontSize: '1.15em', fontWeight: 700 }}>
                        ₪{item.promotionPrice.toFixed(2)}
                      </span>
                      {item.promotionSavings > 0 && (
                        <span style={{ color: '#1c7a3b', fontSize: '0.82em' }}>
                          חסכון ₪{item.promotionSavings.toFixed(2)}
                        </span>
                      )}
                      <ReportButton itemCode={item.itemCode} storeId={storeId} chain={chain} price={item.promotionPrice} itemName={item.itemName} />
                    </div>
                    {item.promotionDescription && (
                      <div
                        dir="rtl"
                        style={{
                          fontSize: '0.8em', color: 'var(--apple-text-2)',
                          borderTop: '1px solid var(--apple-border-subtle)', paddingTop: 6, marginTop: 6
                        }}
                      >
                        {item.promotionDescription}
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
          <Paginator page={page} total={data.total} pageSize={PROMO_PAGE_SIZE} onChange={setPage} />
        </>
      )}
    </div>
  );
}

function InfoTab({ storeDetails, effectiveDistanceText, showDriveTime }) {
  return (
    <Row className="mt-2">
      <Col md={6} className="mb-4">
        <h5 className="mb-3">פרטי החנות</h5>
        <ListGroup variant="flush">
          <ListGroup.Item className="px-0 py-2">
            <span className="text-muted me-2 small">מזהה:</span>
            <span style={{ fontFamily: 'monospace' }}>{storeDetails.store_id}</span>
          </ListGroup.Item>
          <ListGroup.Item className="px-0 py-2">
            <span className="text-muted me-2 small">Composite ID:</span>
            <span style={{ fontFamily: 'monospace', userSelect: 'all' }}>
              {storeDetails.chain}_{storeDetails.store_id}
            </span>
          </ListGroup.Item>
          <ListGroup.Item className="px-0 py-2" dir="rtl">
            <span className="text-muted ms-2 small">כתובת:</span>
            {storeDetails.address}
          </ListGroup.Item>
          <ListGroup.Item className="px-0 py-2" dir="rtl">
            <span className="text-muted ms-2 small">עיר:</span>
            {storeDetails.city}
          </ListGroup.Item>
          {storeDetails.zipcode && storeDetails.zipcode !== 'N/A' && (
            <ListGroup.Item className="px-0 py-2">
              <span className="text-muted me-2 small">מיקוד:</span>
              {storeDetails.zipcode}
            </ListGroup.Item>
          )}
          <ListGroup.Item className="px-0 py-2" dir="rtl">
            <span className="text-muted ms-2 small">רשת:</span>
            {storeDetails.chain_name}
          </ListGroup.Item>
          {storeDetails.subchain_name && (
            <ListGroup.Item className="px-0 py-2" dir="rtl">
              <span className="text-muted ms-2 small">תת-רשת:</span>
              {storeDetails.subchain_name}
            </ListGroup.Item>
          )}
          {effectiveDistanceText && (
            <ListGroup.Item className="px-0 py-2">
              <span className="text-muted me-2 small">מרחק:</span>
              {effectiveDistanceText}
              {showDriveTime && storeDetails.estimated_drive_text && (
                <span className="text-muted ms-2 small">({storeDetails.estimated_drive_text})</span>
              )}
            </ListGroup.Item>
          )}
        </ListGroup>
      </Col>
      <Col md={6} className="mb-4">
        <h5 className="mb-3">מיקום</h5>
        <Card>
          <Card.Body
            className="d-flex flex-column align-items-center justify-content-center text-center"
            style={{ height: 180 }}
          >
            <div dir="rtl" style={{ color: 'var(--apple-text-2)', fontSize: 'var(--font-body)' }}>
              {storeDetails.address}, {storeDetails.city}
            </div>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
}

const TAB_INFO = 'info';
const TAB_PRODUCTS = 'products';
const TAB_PROMOTIONS = 'promotions';

const StoreDetails = () => {
  const { storeId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Chain from URL query param is available immediately — used to enable tabs before API responds
  const chainFromUrl = searchParams.get('chain') || '';
  const [storeDetails, setStoreDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(TAB_INFO);
  const { computeDistance } = useHomeLocation();

  useEffect(() => {
    const fetchStoreDetails = async () => {
      try {
        setLoading(true);
        // Pass chain so the server does an exact lookup, not a linear scan
        const details = await getStoreDetails(storeId, chainFromUrl);
        setStoreDetails(details);
      } catch (err) {
        console.error('Error fetching store details:', err);
        setError('לא ניתן לטעון את פרטי החנות. אנא נסה שוב מאוחר יותר.');
      } finally {
        setLoading(false);
      }
    };
    fetchStoreDetails();
  }, [storeId, chainFromUrl]);

  if (loading) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-2 text-muted">טוען פרטי חנות...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-5">
        <Card className="text-center border-danger">
          <Card.Body>
            <Card.Title className="text-danger">שגיאה</Card.Title>
            <Card.Text>{error}</Card.Text>
            <Button variant="primary" onClick={() => navigate(-1)}>חזרה</Button>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  if (!storeDetails) {
    return (
      <Container className="mt-5">
        <Card className="text-center">
          <Card.Body>
            <Card.Title>חנות לא נמצאה</Card.Title>
            <Button variant="primary" onClick={() => navigate(-1)}>חזרה</Button>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  // Prefer chain from URL (available immediately); fall back to what the API returned
  const chain = chainFromUrl || storeDetails.chain;

  // Override distance display when a custom home location is active
  const customDist = computeDistance(storeDetails.lat, storeDetails.lon);
  const effectiveDistanceText = customDist != null
    ? `${customDist.toFixed(1)} ק"מ`
    : storeDetails.distance_text;

  const tabs = [
    { key: TAB_INFO, label: 'פרטי חנות' },
    { key: TAB_PRODUCTS, label: 'מוצרים' },
    { key: TAB_PROMOTIONS, label: 'מבצעים' },
  ];

  return (
    <Container className="mt-4 mb-5" style={{ maxWidth: 1100 }}>
      <Button variant="outline-secondary" size="sm" onClick={() => navigate(-1)} className="mb-3">
        ← חזרה לחנויות
      </Button>

      {/* Header */}
      <Card className="mb-4 store-details-header">
        <Card.Body className="py-3 px-4">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div>
              <h2 style={{ color: 'var(--apple-text)', fontWeight: 600, letterSpacing: '-0.5px', marginBottom: 4 }} dir="rtl">{storeDetails.store_name}</h2>
              <div className="d-flex gap-2 align-items-center flex-wrap">
                {storeDetails.chain_name && (
                  <span className="chain-badge">
                    <span className={`chain-dot chain-dot-${storeDetails.chain || 'default'}`}></span>
                    {storeDetails.chain_name}
                  </span>
                )}
                {storeDetails.city && (
                  <span style={{ fontSize: 'var(--font-secondary)', color: 'var(--apple-text-2)' }} dir="rtl">
                    {storeDetails.city}
                  </span>
                )}
                {effectiveDistanceText && (
                  <span style={{ fontSize: 'var(--font-secondary)', color: 'var(--apple-text-3)' }}>
                    {effectiveDistanceText}
                    {!customDist && storeDetails.estimated_drive_text && ` · ${storeDetails.estimated_drive_text}`}
                  </span>
                )}
              </div>
            </div>
            <Button variant="outline-secondary" size="sm" onClick={() => navigate(`/compare/${storeDetails.store_id}`)}>
              השווה מחירים
            </Button>
          </div>
        </Card.Body>
      </Card>

      {/* Custom tab bar */}
      <div className="store-tab-bar">
        <div className="d-flex gap-0">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => !tab.disabled && setActiveTab(tab.key)}
              disabled={tab.disabled}
              className={`store-tab-btn${activeTab === tab.key ? ' active' : ''}`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      {activeTab === TAB_INFO && <InfoTab storeDetails={storeDetails} effectiveDistanceText={effectiveDistanceText} showDriveTime={customDist == null} />}
      {activeTab === TAB_PRODUCTS && <ProductsTab storeId={storeId} chain={chain} navigate={navigate} />}
      {activeTab === TAB_PROMOTIONS && <PromotionsTab storeId={storeId} chain={chain} navigate={navigate} />}
    </Container>
  );
};

export default StoreDetails;
